// Limpeza do estoque: os negócios que JÁ estavam em Follow-up quando a
// sistemática nasceu (05/10/2026) entraram desligados. Aqui se decide cada um:
// perder ("Sem retorno do cliente"), ligar por tarefa ou por e-mail, ou deixar
// como está. A sugestão vem de quanto tempo o negócio está parado.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { App as AntApp, Alert, Button, InputNumber, Select, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { fmtDataBr } from '../../utils/dataFmt';
import { valorCompacto } from '../comercial-pipeline/saudeNegocio';
import { fu, erroDe, type ItemLimpeza } from './api';
import { acoesParaAplicar, totaisDaLimpeza, type AcaoLimpeza } from './regras';

const { Text } = Typography;

const OPCOES: { value: AcaoLimpeza; label: string }[] = [
  { value: 'perder', label: '✗ Perder — sem retorno' },
  { value: 'manual', label: '📋 Ligar — tarefa' },
  { value: 'automatico', label: '✉️ Ligar — e-mail automático' },
  { value: 'desligado', label: 'Deixar como está' },
];

export default function AbaLimpeza() {
  const { message, modal } = AntApp.useApp();
  const [dias, setDias] = useState(90);
  const [diasCalculado, setDiasCalculado] = useState(90);
  const [itens, setItens] = useState<ItemLimpeza[]>([]);
  const [escolha, setEscolha] = useState<Record<number, AcaoLimpeza>>({});
  const [carregando, setCarregando] = useState(true);
  const [aplicando, setAplicando] = useState(false);

  const carregar = useCallback(async (d: number) => {
    setCarregando(true);
    try { const r = await fu.limpezaPrevia(d); setItens(r.itens); setEscolha({}); setDiasCalculado(r.dias_sem_movimento); }
    catch (e) { message.error(`Não consegui carregar: ${erroDe(e)}`); }
    finally { setCarregando(false); }
  }, [message]);
  useEffect(() => { carregar(dias); }, [carregar]); // eslint-disable-line react-hooks/exhaustive-deps

  const totais = useMemo(() => totaisDaLimpeza(itens, escolha), [itens, escolha]);

  const aplicar = () => {
    const acoes = acoesParaAplicar(itens, escolha);
    if (!acoes.length) { message.info('Nada para aplicar — todos estão como “deixar como está”.'); return; }
    modal.confirm({
      title: 'Aplicar a limpeza?',
      width: 520,
      content: (
        <div>
          {totais.perder.n > 0 && <p><b>{totais.perder.n}</b> negócio(s) viram <b>perdidos</b> com o motivo “Sem retorno do cliente” ({valorCompacto(totais.perder.valor)}).</p>}
          {totais.manual.n > 0 && <p><b>{totais.manual.n}</b> ganham follow-up por <b>tarefa</b> — a primeira vence hoje.</p>}
          {totais.automatico.n > 0 && <p><b>{totais.automatico.n}</b> ganham <b>e-mail automático</b> — o primeiro sai na próxima janela (se o envio estiver ligado na regra).</p>}
          <p style={{ marginBottom: 0 }}>Fica registrado na linha do tempo de cada negócio. Um perdido volta pelo próprio negócio, se precisar.</p>
        </div>
      ),
      okText: 'Aplicar', cancelText: 'Voltar',
      onOk: async () => {
        setAplicando(true);
        try {
          const r = await fu.limpezaAplicar(acoes);
          message.success(`${r.perdidos} perdido(s), ${r.ligados} ligado(s)${r.ignorados ? `, ${r.ignorados} já não estavam em Follow-up` : ''}.`);
          await carregar(dias);
        } catch (e) { message.error(erroDe(e)); }
        finally { setAplicando(false); }
      },
    });
  };

  const colunas: ColumnsType<ItemLimpeza> = [
    {
      title: 'Negócio', key: 'negocio',
      render: (_, i) => (
        <div style={{ minWidth: 0 }}>
          <div>{i.titulo} {i.do_ploomes && <Tag style={{ marginInlineStart: 4 }}>Ploomes</Tag>}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>{[i.cliente, i.responsavel].filter(Boolean).join(' · ')}</Text>
        </div>
      ),
    },
    { title: 'Valor', key: 'valor', width: 110, align: 'right', render: (_, i) => valorCompacto(i.valor_estimado) },
    {
      title: 'Parado', key: 'parado', width: 150,
      sorter: (a, b) => a.dias_parado - b.dias_parado, defaultSortOrder: 'descend',
      render: (_, i) => <div><div>{i.dias_parado} dias</div><Text type="secondary" style={{ fontSize: 12 }}>desde {fmtDataBr(i.ultimo_movimento)}</Text></div>,
    },
    {
      title: 'Contato', key: 'contato', width: 200,
      render: (_, i) => i.contato_email ? <Text style={{ fontSize: 12 }}>{i.contato_email}</Text> : <Text type="warning" style={{ fontSize: 12 }}>sem e-mail — só tarefa</Text>,
    },
    {
      title: 'O que fazer', key: 'acao', width: 240,
      render: (_, i) => (
        <Select size="small" style={{ width: 225 }} value={escolha[i.id] || i.sugestao}
          options={OPCOES.map(o => ({ ...o, disabled: o.value === 'automatico' && !i.contato_email }))}
          onChange={(v: AcaoLimpeza) => setEscolha(prev => ({ ...prev, [i.id]: v }))} />
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Alert type="info" showIcon
        message="Os negócios que já estavam em Follow-up entraram desligados — nenhum recebeu tarefa ou e-mail sozinho."
        description="A sugestão: parado há mais dias que o limite → perder por falta de retorno; o resto → ligar por tarefa. Ajuste linha a linha e aplique." />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <Space wrap>
          Sugerir “perder” para parados há
          <InputNumber min={1} max={720} value={dias} onChange={v => v && setDias(Number(v))} style={{ width: 90 }} suffix="dias" />
          <Button onClick={() => carregar(dias)} loading={carregando} type={dias !== diasCalculado ? 'primary' : 'default'}>Recalcular</Button>
          {dias !== diasCalculado && <Text type="warning" style={{ fontSize: 12 }}>a sugestão abaixo ainda é para {diasCalculado} dias</Text>}
        </Space>
        <div style={{ flex: 1 }} />
        <Text>
          {itens.length} no estoque · perder {totais.perder.n} ({valorCompacto(totais.perder.valor)}) ·
          tarefa {totais.manual.n} · e-mail {totais.automatico.n} · como está {totais.desligado.n}
        </Text>
        <Button type="primary" onClick={aplicar} loading={aplicando} disabled={!itens.length}>Aplicar</Button>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <Table rowKey="id" size="small" loading={carregando} dataSource={itens} columns={colunas}
          pagination={false}
          locale={{ emptyText: 'Estoque limpo — nenhum negócio antigo esperando decisão.' }} />
      </div>
    </div>
  );
}
